import React from 'react';

const Rule: React.FC = () => {
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-primary mb-4">She Embrace Cup - WEURO 2025 Game</h1>
        
        <div className="prose prose-lg text-gray-600 mb-8">
          <p>Welcome to a unique prediction game that celebrates women's football and promotes the values of love and peace. 
          In this special edition for WEURO 2025, we're not just predicting matches – we're embracing the beautiful spirit of 
          the women's game. Choose your teams with both strategy and heart: support the powerhouses with your regular picks, 
          follow your heart with your Love team, and champion unity with your Peace team. Together, let's celebrate the 
          skill, passion, and unity that women's football brings to the beautiful game! 🌟⚽️🕊️</p>
        </div>

        <div className="space-y-8">
          {/* Team Selection */}
          <section>
            <h2 className="text-2xl font-semibold mb-4">Team Selection</h2>
            <div className="bg-gray-50 p-4 rounded-lg space-y-4">
              <p>Each participant must select:</p>
              <div className="space-y-3">
                <div>
                  <p className="font-medium">3 Regular Teams (with different multipliers)</p>
                  <ul className="text-sm text-gray-600 list-disc pl-6">
                    <li>First team: 1.5× points</li>
                    <li>Second team: 1.2× points</li>
                    <li>Third team: 1.2× points</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium">1 Love Team</p>
                  <p className="text-sm text-gray-600">Base points with no multiplier - if this team achieves its bonus condition, the Peace team's points will be doubled (2.0×)</p>
                </div>
                <div>
                  <p className="font-medium">1 Peace Team</p>
                  <p className="text-sm text-gray-600">Base points, doubled (2.0×) if Love team achieves its bonus condition</p>
                </div>
              </div>

              <div className="mt-6 bg-blue-50 p-4 rounded-lg">
                <p className="font-medium mb-2">Score Calculation Example:</p>
                <div className="text-sm text-gray-800 space-y-2">
                  <p className="font-mono">Total Score = (Team1 × 1.5) + (Team2 × 1.2) + (Team3 × 1.2) + Love + (Peace × multiplier)</p>
                  <p>Example:</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Regular Team 1 (Spain): 30 × 1.5 = 45 points</li>
                    <li>Regular Team 2 (France): 55 × 1.2 = 66 points</li>
                    <li>Regular Team 3 (Germany): 58 × 1.2 = 69.6 points</li>
                    <li>Love Team (England): 72 points (Champion and achieved bonus)</li>
                    <li>Peace Team (Sweden): 156 × 2.0 = 312 points (doubled because Love team got bonus)</li>
                    <li>Total: 45 + 66 + 69.6 + 72 + 312 = 564.6 points</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Points System */}
          <section>
            <h2 className="text-2xl font-semibold mb-4">Points System</h2>
            
            <div className="mb-6">
              <h3 className="text-xl font-medium mb-3">Tournament Progress Points</h3>
              <div className="bg-gray-50 p-4 rounded-lg overflow-x-auto">
                <table className="w-full table-auto border-collapse border border-gray-200">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-2">Team</th>
                      <th className="border p-2">Quarter-finals</th>
                      <th className="border p-2">Semi-finals</th>
                      <th className="border p-2">Finals</th>
                      <th className="border p-2">Champion</th>
                      <th className="border p-2">Bonus Condition (20 points)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border p-2">Spain</td>
                      <td className="border p-2">1</td>
                      <td className="border p-2">5</td>
                      <td className="border p-2">15</td>
                      <td className="border p-2">30</td>
                      <td className="border p-2">Score at least 13 goals</td>
                    </tr>
                    <tr>
                      <td className="border p-2">England</td>
                      <td className="border p-2">4</td>
                      <td className="border p-2">13</td>
                      <td className="border p-2">25</td>
                      <td className="border p-2">52</td>
                      <td className="border p-2">Comeback victory after trailing</td>
                    </tr>
                    <tr>
                      <td className="border p-2">France</td>
                      <td className="border p-2">4</td>
                      <td className="border p-2">13</td>
                      <td className="border p-2">26</td>
                      <td className="border p-2">55</td>
                      <td className="border p-2">Advance at least 2 stages more than Germany</td>
                    </tr>
                    <tr>
                      <td className="border p-2">Germany</td>
                      <td className="border p-2">3</td>
                      <td className="border p-2">15</td>
                      <td className="border p-2">28</td>
                      <td className="border p-2">58</td>
                      <td className="border p-2">Advance at least 3 stages more than France</td>
                    </tr>
                    <tr>
                      <td className="border p-2">Sweden</td>
                      <td className="border p-2">7</td>
                      <td className="border p-2">30</td>
                      <td className="border p-2">65</td>
                      <td className="border p-2">156</td>
                      <td className="border p-2">Better group stage performance than Norway</td>
                    </tr>
                    <tr>
                      <td className="border p-2">Netherlands</td>
                      <td className="border p-2">12</td>
                      <td className="border p-2">34</td>
                      <td className="border p-2">72</td>
                      <td className="border p-2">176</td>
                      <td className="border p-2">Qualify for knockout stage in extra time OR win first knockout match</td>
                    </tr>
                    <tr>
                      <td className="border p-2">Denmark</td>
                      <td className="border p-2">10</td>
                      <td className="border p-2">43</td>
                      <td className="border p-2">97</td>
                      <td className="border p-2">264</td>
                      <td className="border p-2">No red cards; total yellow cards less than 8</td>
                    </tr>
                    <tr>
                      <td className="border p-2">Italy</td>
                      <td className="border p-2">11</td>
                      <td className="border p-2">28</td>
                      <td className="border p-2">97</td>
                      <td className="border p-2">299</td>
                      <td className="border p-2">Win group stage with positive goal difference</td>
                    </tr>
                    <tr>
                      <td className="border p-2">Norway</td>
                      <td className="border p-2">6</td>
                      <td className="border p-2">27</td>
                      <td className="border p-2">98</td>
                      <td className="border p-2">306</td>
                      <td className="border p-2">Better group stage performance than Sweden</td>
                    </tr>
                    <tr>
                      <td className="border p-2">Iceland</td>
                      <td className="border p-2">8</td>
                      <td className="border p-2">37</td>
                      <td className="border p-2">144</td>
                      <td className="border p-2">475</td>
                      <td className="border p-2">Score at least 5 points in group stage</td>
                    </tr>
                    <tr>
                      <td className="border p-2">Switzerland</td>
                      <td className="border p-2">12</td>
                      <td className="border p-2">44</td>
                      <td className="border p-2">153</td>
                      <td className="border p-2">485</td>
                      <td className="border p-2">Keep one clean sheet</td>
                    </tr>
                    <tr>
                      <td className="border p-2">Portugal</td>
                      <td className="border p-2">19</td>
                      <td className="border p-2">53</td>
                      <td className="border p-2">201</td>
                      <td className="border p-2">715</td>
                      <td className="border p-2">Score against Spain OR qualify</td>
                    </tr>
                    <tr>
                      <td className="border p-2">Belgium</td>
                      <td className="border p-2">24</td>
                      <td className="border p-2">68</td>
                      <td className="border p-2">274</td>
                      <td className="border p-2">1114</td>
                      <td className="border p-2">At least one player scores 2+ goals</td>
                    </tr>
                    <tr>
                      <td className="border p-2">Finland</td>
                      <td className="border p-2">17</td>
                      <td className="border p-2">78</td>
                      <td className="border p-2">321</td>
                      <td className="border p-2">1195</td>
                      <td className="border p-2">Score at least one goal from open play</td>
                    </tr>
                    <tr>
                      <td className="border p-2">Poland</td>
                      <td className="border p-2">58</td>
                      <td className="border p-2">319</td>
                      <td className="border p-2">1196</td>
                      <td className="border p-2">4990</td>
                      <td className="border p-2">Score at least 1 point in group stage</td>
                    </tr>
                    <tr>
                      <td className="border p-2">Wales</td>
                      <td className="border p-2">99</td>
                      <td className="border p-2">451</td>
                      <td className="border p-2">1483</td>
                      <td className="border p-2">7133</td>
                      <td className="border p-2">Concede less than 9 goals in group stage OR qualify</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="mt-4 space-y-2 text-sm text-gray-600">
                <p>Notes:</p>
                <ul className="list-disc pl-6">
                  <li>Group stage ranking: If points are equal, goal difference is the tiebreaker. If goal difference is equal, goals scored is the tiebreaker. If all are equal, teams are considered tied and no bonus is awarded.</li>
                  <li>For Denmark's red card rule: Only counts for players (on field and bench players), not coaches.</li>
                  <li>Comeback victory: The reversal must occur from a losing position at any point during the match and win at the end. For extra time matches, a comeback during extra time also counts; however, winning on penalties after being tied does not count as a comeback.</li>
                  <li>Tournament stages: Champion {`>`} Runner-up {`>`} Semi-finals {`>`} Quarter-finals {`>`} Group stage. For example, if France reaches the final and Germany reaches quarter-finals, France has advanced two more stages than Germany.</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Rule; 